import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo } from "react";

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Swal from "sweetalert2";

import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

//import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';

import { useDispatch, useSelector } from "react-redux";
import { fetchDiscountsData } from "../../store/discounts/actions";

const Discounts = props => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data, loading, error } = useSelector(state => state.Discounts);

  useEffect(() => {
    dispatch(fetchDiscountsData());
  }, [dispatch]);

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");
          const response = await fetch(
            `${process.env.REACT_APP_WEB_APP_URL}/api/web/discounts/delete/${id}`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const result = await response.json();

          if (response.status === 200) {
            toast.success('Discount has been deleted.', { autoClose: 3000 });
            dispatch(fetchDiscountsData());
          } else {
            toast.error(result?.message || 'Failed to delete', { autoClose: 3000 });
          }
        } catch (err) {
          console.error(err);
          toast.error("Failed to delete discount", { autoClose: 3000 });
        }
      }
    });
  };

  const handleRestore = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f8b425",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, restore it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem("authToken");
          const response = await fetch(
            `${process.env.REACT_APP_WEB_APP_URL}/api/web/promotions/restore/${id}`,
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const result = await response.json();

          if (response.status === 200) {
            toast.success('Promotion has been restored.', { autoClose: 3000 });
            dispatch(fetchPromotionsData());
          } else {
            toast.error(result?.message || 'Failed to restored', { autoClose: 3000 });
          }
        } catch (err) {
          console.error(err);
          toast.error("Failed to restored promotion", { autoClose: 3000 });
        }
      }
    });
  };

  const columns = useMemo(
    () => [
      {
        header: 'Discount Name',
        accessorKey: 'title',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Discount Type',
        accessorKey: 'discount_type',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Discount Value',
        accessorKey: 'discount_value',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Start Date',
        accessorKey: 'start_date',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'End Date',
        accessorKey: 'end_date',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Count',
        accessorKey: 'count',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Status',
        accessorKey: 'status',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Action',
        id: 'action',
        cell: ({ row }) => {
          const discountId = row.original?.id;
          const is_deleted = row.original?.is_deleted;
          return (
            <div className="d-flex gap-2">
              {/* <button
                className="btn btn-sm btn-success"
                onClick={() => navigate(`/promotions/edit/${discountId}`)}
              >
                <i className="mdi mdi-pencil"></i> Edit
              </button> */}
              {is_deleted == 1 ? (
                <button
                  className="btn btn-sm btn-warning"
                  onClick={() => handleRestore(discountId)}
                >
                  <i className="mdi mdi-restore"></i> Restore
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(discountId)}
                >
                  <i className="mdi mdi-delete"></i> Delete
                </button>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const totalRecords = data?.data?.length ?? 0;

  const transformedData = useMemo(() => {
    return data?.data?.map(item => {
      return {
        title: item.name || "N/A",
        discount_type: item.discount_type || "N/A",
        discount_value: item.discount_value || "N/A",
        start_date: item.start_date != null ? item.start_date : "-",
        end_date: item.end_date != null ? item.end_date : "-",
        count: item.count || "N/A",
        status: item.status || "N/A",
        ...item
      };
    });
  }, [data]);

  document.title = "Discounts | Moeen";
  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Home" breadcrumbItem="Discounts" />
        <div className="float-end d-none d-md-block">
          <Link to="/discounts/create"
            color="primary"
            className="btn btn-primary text-white waves-effect waves-light"
          >
            Add Discount
          </Link>
        </div>
        {loading ? (
          <Skeleton count={10} height={40} />
        ) : (
          <TableContainer
            columns={columns}
            data={transformedData || []}
            isGlobalFilter={true}
            isPagination={true}
            SearchPlaceholder={`Looking for something? ${totalRecords} results loaded.`}
            pagination="pagination"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        )}
      </div>
    </div>
  );
};

Discounts.propTypes = {
  t: PropTypes.any
};

export default Discounts;